import { format, parseISO, startOfDay } from "date-fns";
import { it, enGB } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const TIMES = [];
for (let h = 0; h < 24; h++) {
  TIMES.push(`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`);
}

const CAL_CLASSNAMES = {
  caption_label: "font-serif text-lg tracking-wide text-bone",
  nav_button:
    "h-8 w-8 border border-gold/30 bg-transparent text-bone/70 hover:text-gold hover:border-gold/60 rounded-none",
  head_cell: "text-mute w-9 text-[10px] tracking-[0.15em] uppercase font-normal",
  day: "h-9 w-9 p-0 font-normal text-bone/85 hover:bg-gold/10 hover:text-gold rounded-none",
  day_selected: "bg-gold text-ink hover:bg-gold hover:text-ink rounded-none font-medium",
  day_today: "border border-gold/60 text-gold rounded-none",
  day_outside: "text-mute/40",
  day_disabled: "text-mute/30 opacity-40",
};

export const QuoteDateField = ({ value, onChange, lang = "it", testid = "quote-form-date-input" }) => {
  const loc = lang === "en" ? enGB : it;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="field flex items-center justify-between gap-3 text-left"
          aria-haspopup="dialog"
        >
          <span className={value ? "" : "text-mute/50"}>
            {value
              ? format(parseISO(value), "d MMMM yyyy", { locale: loc })
              : lang === "en"
                ? "Select a date"
                : "Seleziona una data"}
          </span>
          <CalendarIcon size={16} className="flex-none text-gold/80" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-auto border-gold/25 bg-coal p-4 text-bone shadow-2xl"
      >
        <Calendar
          mode="single"
          selected={value ? parseISO(value) : undefined}
          onSelect={(d) => d && onChange(format(d, "yyyy-MM-dd"))}
          disabled={{ before: startOfDay(new Date()) }}
          locale={loc}
          classNames={CAL_CLASSNAMES}
        />
      </PopoverContent>
    </Popover>
  );
};

export const QuoteTimeField = ({ value, onChange, lang = "it", testid = "quote-form-time-input" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger
      data-testid={testid}
      className="field h-auto w-full justify-between gap-3 rounded-none border-0 border-b border-bone/20 bg-transparent px-2 py-3 text-left text-[15px] font-light shadow-none ring-0 focus:ring-0 focus:border-gold data-[placeholder]:text-mute/50 [&>span]:line-clamp-none"
    >
      <SelectValue placeholder={lang === "en" ? "Select a time" : "Seleziona l'orario"} />
    </SelectTrigger>
    <SelectContent className="max-h-64 border-gold/25 bg-coal text-bone">
      {TIMES.map((t) => (
        <SelectItem
          key={t}
          value={t}
          className="rounded-none font-light hover:bg-gold/10 hover:text-gold focus:bg-gold/10 focus:text-gold data-[state=checked]:text-gold"
        >
          {t}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
